import { Icon } from "./Icon";
import { Fragment, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { WorkoutOverviewWindow } from "./WorkoutOverviewWindow";
import { supabase } from "../lib/supabase";
import { fetchAssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import { workoutOverview } from "../lib/workoutPreview";
import { daySections, exerciseCountLabel, type DaySection } from "../lib/calendarTiles";
import { AssignmentMenu } from "./AssignmentMenu";
import { COPY_ITEM, EDIT_ITEM, MOVE_ITEM, canEditWorkout, cardMenuItems, type MenuItem } from "../lib/assignmentActions";
import { useAnyHover } from "../lib/pointerKind";
import {
  SPANS, calendarQueryKeeping, calendarWeeks, dayMonthLabel, tasksFoldLabel, workoutsThenTasks, offsetForDate, rangeLabel,
  viewFromQuery, type CalendarItem, type CalendarView, type Span,
} from "../lib/calendar";
import { useQuerySort } from "../lib/useQuerySort";
import { href, routeQuery } from "../lib/route";
import { appWeekRange, mondayKey } from "../lib/weekLine";
import { shortDate } from "../lib/weekLine";
import { usePhoneWidth } from "../lib/phoneWidth";
import { dayKey, dayKeyOf } from "../lib/days";
import { DayTasksWindow } from "./DayTasksWindow";
import type { Assignment } from "../lib/client";
import { ProposalTiles } from "./ProposalTiles";
import { proposalsLabel, type OverlayPair } from "../lib/proposalOverlay";
import { cellHeadWord, cellState } from "../lib/dayCells";
import { ColumnWorkout, DayColumn, backToStart, useWeekTrees, type WeekTrees } from "./WeekDayColumn";
import { printWorkouts } from "./WorkoutPrintSheet";
import { weekPrintouts } from "../lib/workoutPrintout";
import { DayDragArea, DragItem, DropDay, MoveHandle, useDayArrangement } from "./DayDrag";
import { byDayOrder, copyLabel, moveHandleLabel, type DayArrangement, type DropPlan } from "../lib/dayDrag";

/** A day as the at-rest icons name it: `Tuesday 29/9`. */
function dayWords(dayKey: string): string {
  return `${cellHeadWord(dayKey, 1)} ${dayMonthLabel(dayKey)}`;
}
import {
  BACK_TO_TOP_OF_WEEK, NOTES_MODES, NOTES_MODE_LABEL, SCROLL_MODES, SCROLL_MODE_LABEL, WEEK_MODES, WEEK_MODE_LABEL,
  notesModeFrom, notesModeQueryKeeping, readStoredNotesMode, readStoredScrollMode, readStoredWeekMode,
  scrollModeFrom, scrollModeQueryKeeping, storeNotesMode, storeScrollMode, storeWeekMode, visibleWorkoutIds, weekLayout, weekModeFrom,
  weekModeQueryKeeping, type NotesMode, type ScrollMode, type WeekMode,
} from "../lib/weekView";
import { readPref, writePref } from "../lib/viewPrefs";
import { CalendarOptions } from "./CalendarOptions";
import { useCarryFeedback, type CarryFeedback } from "./useCarryFeedback";
import { WeekEditorProvider, useWeekEditor, type EditorDay, type WeekWrite } from "./WeekEditor";
import { WeekLibrary } from "./WeekLibrary";
import { SaveWeekAsProgram, type WeekToSave } from "./SaveWeekAsProgram";

/** The viewer's own store, or undefined where there is none (a test, a sandbox) — every read and write is try/caught. */
function viewerStore(): Storage | undefined {
  try { return window.localStorage; } catch { return undefined; }
}

/** Monday-first, the one order this product's calendars use. */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* THE TRAINING CALENDAR — their surface (EVERFIT-MAP §2b-bis), our rules.
 *
 * COPIED: Monday-first, a tall cell per day, the date top-left, today marked, the `TODAY` button,
 * the `‹ range ›` walk, the `Assignment ⇄ History` pair and the `1 / 2 / 4 Week` spans.
 *
 * 🔴 NOT COPIED, AND THE REASON IS MEASURED IN BOTH CASES:
 *   * their `+` appears on HOVER ONLY. §12 executed
 *     `main a[href],main button,main [tabindex]` on this very screen and got ZERO — so a coach on
 *     a keyboard cannot reach the one control the screen exists for.
 *     🔴 **W22(g), 2026-09-19 — THE PICTURE IS NOW THEIRS AND THE SEMANTICS ARE STILL OURS.**
 *     Fable ruled the `+` is built as their depth-2 shape: hidden at rest, revealed by hovering
 *     the day cell. So it is a real `<button>` in every cell, in the DOM and in the TAB ORDER,
 *     named for the day it adds to, drawn at `opacity: 0` and revealed by the cell's hover OR by
 *     its own focus (`.workouts-peek`'s pattern, W17d). What is copied is what the screen LOOKS
 *     like; what is not copied is a control a keyboard cannot reach.
 *     ⚠️ **AND IT HAS A CALLER AT LAST.** `onAddTo` existed as an optional prop for weeks with
 *     none, so the control this comment called the cheapest falsifiable "better than Everfit" on
 *     the product had never been drawn once. `ClientTraining` passes it; it opens the assign
 *     panel PRE-FILLED with the day, which is why `AssignPanel` now takes days outside its own
 *     fortnight (`offeredWithAdded`) — the calendar can show four weeks from any date, and a `+`
 *     opening a form that cannot hold its day would be the same dead control one level down.
 *   * their `4 Week` ceiling is a PAYWALL ("Upgrade to schedule more than 4 weeks ahead"). We take
 *     the same three spans because they suit a coach's eye, not because a price says so.
 *
 * ⚠️ THE MODEL DOES NOT MOVE: the PROGRAM is the truth and this RENDERS it (his 2026-08-27
 * sentence). Nothing here decides what a week contains.
 */
export function TrainingCalendar(props: {
  assignments: Assignment[];
  trainingDays: Set<string>;
  today: Date;
  onAddTo?: (dayKey: string) => void;
  /** W11b. Absent = no `⋯` is drawn at all, which is how this ships before its caller exists. */
  onAssignmentAction?: (assignmentId: string, item: MenuItem) => void;
  /** W22(f) — `assignmentId → coach_workouts.id`, for the expanded cell's tiles. */
  workoutIds?: Record<string, string>;
  /** W22(b) — a tile click. Absent means the tiles are read-only, which is a real state. */
  onEditExercise?: (workoutId: string, exerciseRowId: string) => void;
  /** W22(d) — whose calendar this is, so the view can be written back into that client's URL. */
  clientId: string;
  /** QUEUE N2b-2 — the client's name for the printed pages' corner (Everfit's). Absent = the corner is left blank. */
  clientName?: string;
  /** Job 3 step 5 (one calendar) — a new task on a day: the page's `TaskEditor` for THAT day key, with no default (a
   *  hard-coded today once wrote a task into the wrong week). Absent = no task add control at all: a non-active link. */
  onAddTask?: (dayKey: string) => void;
  /** Job 3 step 5 — called after a task is removed in a day's tasks window. Absent = the `+ N tasks` line is plain text. */
  onTasksChanged?: () => void;
  /** Job 3 step 5 cut 2 — THIS week's empty sentence ("Nothing assigned this week"), drawn as the caption under this week's
   *  row in both views. Absent = the week is not empty (the page's head says its summary). */
  emptyWeekLine?: string;
  /**
   * W72 step 2 — THE PROPOSALS OVERLAY, when the coach has it on: next week's copies paired with their
   * template's previous copy. Absent = the plain manual calendar (his amendment: an add-on, off by default).
   */
  overlay?: { pairs: readonly OverlayPair[]; counts: Readonly<Record<string, number>> };
  /** The overlay's switch, drawn at the bar's right. Absent = no switch (a page with no coach preference). */
  overlayToggle?: { on: boolean; disabled: boolean; onToggle: (next: boolean) => void };
  /** Called after a Hold writes, so the page re-reads the client. */
  onOverlayWritten?: () => void;
  /** W137 cut 2 — a drop's writes (`dayDrag.dropPlan`): the page performs them and answers whether they landed. Absent =
   *  no handles are drawn. */
  onMoveWorkout?: (plan: DropPlan) => Promise<boolean>;
  /** Week editor stage 1 (2026-10-19) — the page's ONE write (`write_week_trees`). Absent = Extended only reads (the Program
   *  board). */
  onWriteWeek?: WeekWrite;
  /** Week editor stage 2 — the library panel's Workouts tab assigns a template to a day (the page's own assign). */
  onAssignWorkout?: (w: { id: string; rootId: string; name: string }, dayKey: string, index: number) => Promise<{ said: string; ok: boolean }>;
  /** A write created or dropped a client copy: the page re-reads the client's assignments. */
  onWeekStructureChanged?: () => void;
}) {
  const { assignments, trainingDays, today, onAddTo, onAssignmentAction, clientId, clientName, workoutIds,
          onEditExercise, onAddTask, onTasksChanged, emptyWeekLine, overlay, onOverlayWritten, overlayToggle, onMoveWorkout,
          onWriteWeek, onWeekStructureChanged, onAssignWorkout } = props;

  /* THE DAY'S "N proposals" — read ONCE by the page (`useProposalCounts`) and handed down, so the head's total
     and the cells' marks are one read; absent until it lands, never a placeholder. */
  const proposalCounts = overlay?.counts ?? {};

  /* 🔴 W22(d) — THE SPAN **AND THE DATE** COME FROM THE URL AND GO BACK TO IT. §2b-ter: theirs
     carries `?tab=assignment&date=2026-09-07`, two parameters, so a coach can send a colleague a
     link to a SPECIFIC WEEK. Read as ONE value, because two `useQuerySort`s over one hash can
     answer from two different reads of it.
     ⚠️ THROUGH `useQuerySort`, which listens to `hashchange`. A value read in a `useState`
     initialiser is read ONCE and these screens do not remount on navigation — the exact bug
     DRIVEN on 2026-09-18, where a pasted sort parsed correctly and changed nothing. */
  /* W140: no span in the URL → the coach's remembered one (`viewPrefs`), never a hard 2 on every new client. */
  const [view, setViewState] = useQuerySort<CalendarView>((q) => viewFromQuery(q, readPref("weekSpan")), routeQuery);
  /* 🔴 W21 (B2) — A PHONE DRAWS ONE WEEK, AND THE LABEL HAS TO AGREE WITH IT. The app's client
     screen is a week navigator: `‹ Sep 14 – 20 ⌄ ›` over seven day rows. CSS could hide the
     second week; it could not stop the range saying `Mon 14 Sep – Sun 27 Sep` above one week of
     rows, which is a sentence that is simply false. So the span is capped in JavaScript, at this
     one seam, and everything downstream — `weeks`, the label, the steppers — reads the capped
     value and cannot disagree with what is drawn.
     ⚠️ THE STORED VALUE IS NOT TOUCHED. `view.span` still holds what the URL says, so a link
     opened on a phone and re-opened on a laptop is the fortnight the coach sent. */
  const isPhone = usePhoneWidth();
  const span = isPhone ? 1 : view.span;
  /* THE OFFSET IS DERIVED, NEVER STORED. A link carrying an offset means a different fortnight
     tomorrow than it did today; a date means the same week for ever. */
  const offset = view.date === null ? 0 : offsetForDate(today, view.date);

  function setView(next: CalendarView) {
    setViewState(next);
    /* `replaceState`, not `pushState`: changing which weeks you are looking at is not a place you
       navigated to, so Back must still take the coach OFF this screen rather than through six
       ranges. It also does not fire `hashchange`, so this cannot loop through the listener. */
    window.history.replaceState(null, "",
      `${href({ name: "client", clientId })}${calendarQueryKeeping(routeQuery(window.location.hash), next.span, next.date ?? mondayKey(today, 0))}`);
  }
  const setSpan = (s: Span) => { writePref("weekSpan", String(s)); setView({ span: s, date: view.date ?? mondayKey(today, 0) }); };
  /* ⚠️ `view.span`, NEVER THE CAPPED `span`. Paging on a phone must not silently rewrite a
     fortnight link into a one-week one — the cap is a rendering decision about this screen, and
     a control that writes it back to the URL turns it into the coach's stored choice. */
  const setOffset = (f: (n: number) => number) => {
    setView({ span: view.span, date: mondayKey(today, f(offset)) });
  };

  /* 🔴 W137 (R1, R5b) — MINIMIZED · EXTENDED, and in Extended SCROLL: EACH DAY · TOGETHER. Each is ONE page-level choice,
     stated in the URL beside the span and the date (a pasted link opens the same view) and remembered per viewer. The URL
     wins; a link with neither falls back to what this viewer last chose; nobody's page changes until they press Extended. */
  const [mode, setModeState] = useQuerySort<WeekMode>((q) => weekModeFrom(q, readStoredWeekMode(viewerStore())), routeQuery);
  const [scroll, setScrollState] = useQuerySort<ScrollMode>((q) => scrollModeFrom(q, readStoredScrollMode(viewerStore())), routeQuery);
  function setMode(next: WeekMode) {
    setModeState(next);
    storeWeekMode(viewerStore(), next);
    window.history.replaceState(null, "",
      `${href({ name: "client", clientId })}${weekModeQueryKeeping(routeQuery(window.location.hash), next)}`);
  }
  function setScroll(next: ScrollMode) {
    setScrollState(next);
    storeScrollMode(viewerStore(), next);
    window.history.replaceState(null, "",
      `${href({ name: "client", clientId })}${scrollModeQueryKeeping(routeQuery(window.location.hash), next)}`);
  }
  /* His word 2026-10-15 (William's item 3): COACH NOTES: SHOW · HIDE, the same page-level choice — URL, then memory. */
  const [notes, setNotesState] = useQuerySort<NotesMode>((q) => notesModeFrom(q, readStoredNotesMode(viewerStore())), routeQuery);
  function setNotes(next: NotesMode) {
    setNotesState(next);
    storeNotesMode(viewerStore(), next);
    window.history.replaceState(null, "",
      `${href({ name: "client", clientId })}${notesModeQueryKeeping(routeQuery(window.location.hash), next)}`);
  }
  const layout = weekLayout({ mode, scroll, span, narrow: isPhone });
  /* 🔴 W137 cut 1b (Fable, on the cut-1 stills: "the columns get the screen") — in Extended on a wide screen the controls
     row is sticky at the page's top and the day heads stick under it; a week row is the viewport minus that block. The
     block's height is MEASURED (it wraps to two lines at ~1100 px) and handed to the CSS as `--calendar-bar-h`. */
  const bar = useRef<HTMLDivElement>(null);
  const [barH, setBarH] = useState<number | null>(null);
  const stickyBar = layout.kind === "columns" && !layout.oneDayPerRow;
  useLayoutEffect(() => {
    const el = bar.current;
    if (!stickyBar || el === null) { setBarH(null); return; }
    const measure = () => { setBarH(Math.round(el.getBoundingClientRect().height)); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, [stickyBar]);

  const itemsByDay = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const a of assignments) {
      if (a.scheduledDateISO === null) continue;
      const k = dayKey(a.scheduledDateISO);
      if (k === null) continue;
      /* ⚠️ HIDDEN ASSIGNMENTS ARE NOT DRAWN HERE. A row the client never saw is not on their
         calendar — it is on the Training tab's list, where it is labelled as hidden. Drawing it
         would tell the coach the athlete had work they were never shown. */
      if (a.hiddenFromClient) continue;
      const item: CalendarItem = {
        id: a.id,
        name: a.workoutName ?? (a.isTask ? "Task" : "Workout"),
        isTask: a.isTask,
        /* ⚠️ FOR A TASK, A DAY WITH A LOGGED SET IS NOT A TASK THAT WAS DONE — the tick says the weaker thing on purpose:
           a task has no set log of its own, so the mark means "they trained that day" (the Tasks calendar's rule, folded in
           here with it, job 3 step 5). AND NEVER ON AN OPTIONAL ONE (his 2026-10-07): nothing on it may be read as
           progress, so it carries the word instead of the mark. */
        trained: a.isTask ? !a.isOptional && trainingDays.has(k) : trainingDays.has(k),
        optional: a.isTask ? a.isOptional : undefined,
        /* W11b — a task has no `coach_assignments` row to act on, so it carries no id and the
           card draws no `⋯` for it. Absent rather than a placeholder: an id that is not one is
           how a menu ends up writing to something that does not exist. */
        assignmentId: a.isTask ? undefined : a.id,
        /* W22(f) — the tree the expanded cell fetches. A TASK has no workout tree worth
           drawing tiles for (it is one row with no sets), and the map has no entry for one. */
        workoutId: a.isTask ? undefined : workoutIds?.[a.id],
        dayPosition: a.dayPosition ?? 0,
        assignedAtISO: a.assignedAtISO ?? null,
      };
      m.set(k, [...(m.get(k) ?? []), item]);
    }
    /* W137 (R7) — a day's items in their STORED order: `day_position`, then `assigned_at` (his "the top one first"). */
    for (const list of m.values()) {
      list.sort((x, y) => byDayOrder({ dayPosition: x.dayPosition ?? 0, assignedAtISO: x.assignedAtISO ?? null, id: x.id },
                                     { dayPosition: y.dayPosition ?? 0, assignedAtISO: y.assignedAtISO ?? null, id: y.id }));
    }
    return m;
  }, [assignments, trainingDays, workoutIds]);

  const weeks = useMemo(
    () => calendarWeeks(today, span, offset, itemsByDay),
    [today, span, offset, itemsByDay],
  );
  /* R4 — Extended draws every visible day's workouts: ONE batched read for the visible range (and one for their exercises'
     pictures). Minimized reads nothing — the squares stay W115's. */
  const visibleIds = useMemo(() => visibleWorkoutIds(weeks.flatMap((w) => w.days)), [weeks]);
  /* Week editor stage 1 — the editor asks for a re-read after the set window saves, or when a write was refused as stale. */
  const [treesKey, bumpTrees] = useReducer((k: number) => k + 1, 0);
  const trees = useWeekTrees(visibleIds, layout.kind === "columns", treesKey);
  /* CARRY-FEEDBACK — what the client will be shown: under an exercise, the coach's last comment on it (A); under a workout,
     the coach's last review of the same workout (B) — each on its FIRST coming place (`useCarryFeedback`, the phone's own
     rules). Extended only, like the trees. */
  const lastVisibleDay = weeks.at(-1)?.days.at(-1)?.dayKey ?? null;
  const said = useCarryFeedback(clientId, lastVisibleDay, layout.kind === "columns");
  /* QUEUE N2b-2 — what Print prints: the visible days' workouts as Everfit's grid, from the trees just read. Empty until they
     land, and the button is drawn only when there is something (HIG: offer printing only when it is possible). */
  const printable = useMemo(
    () => (trees.status === "ready"
      ? weekPrintouts(weeks.flatMap((w) => w.days), trees.trees,
                      { client: clientName ?? "", equipment: trees.equipment, fields: trees.fields })
      : []),
    [trees, weeks, clientName],
  );
  /* W137 cut 2 — what the drag works on: each visible day's assignments top to bottom, and each one's stored order. */
  const itemById = useMemo(() => new Map(weeks.flatMap((w) => w.days.flatMap((d) => d.items)).map((i) => [i.id, i])), [weeks]);
  const dragArrangement = useMemo(() => dayArrangementOf(weeks), [weeks]);
  const storedOrder = useMemo(() => Object.fromEntries([...itemById.values()].map((i) => [i.id, i.dayPosition ?? 0])), [itemById]);
  /* 🔴 W115 (William, 2026-09-30 ~22:05: *"i don't like when the container becomes super long no, need to keep it square"*)
     — SUPERSEDES W114's lift and, before it, W107's always-drawn list: a card says its date, its mark and its workout titles,
     and a MAGNIFIER beside each title opens the workout in a window (below). So the cards read NOTHING: Fable fix (c)'s
     batched read of the visible days' trees went with the lists it fed, and the window reads its one workout on open.
     W72 step 2's one-by-one toggle, `Open all` and `?open=all` stay retired. */
  /* Two spellings for two different readers, from the ONE date vocabulary. The desktop draws up
     to four weeks, so both ends are spelled in full with their weekdays; a phone draws one
     Monday-to-Sunday week, so the weekdays are the one thing the label need not say. */
  const range = isPhone && weeks[0] !== undefined
    ? appWeekRange(weeks[0].mondayKey)
    : rangeLabel(weeks, shortDate);
  /* W21 (B2) — the app's `⌄` beside the range opens a jump. It is a real disclosure of a real
     `<input type="date">`: the app's caret opens a day picker, and a caret that opened nothing
     would be the `+`-with-no-caller defect W22(g) is this repo's own lesson about. */
  const [jumping, setJumping] = useState(false);
  /* 🔴 W115 — THE WORKOUT A CARD'S MAGNIFIER OPENS: THE library's workout overview window (`WorkoutOverviewWindow`, W17d —
     every section and exercise with its dose, uncapped), reused for the day's COPY, ONE window for the whole calendar. It
     closes back to this page (Close, Escape, the dimmed ground) and focus returns to the magnifier that opened it — a modal
     leaves the page inert while open, so the return waits for the window to be gone. */
  const [look, setLook] = useState<{ workoutId: string; dayKey: string } | null>(null);
  const lookOpener = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (look !== null || lookOpener.current === null) return;
    lookOpener.current.focus();
    lookOpener.current = null;
  }, [look]);
  const lookPair = look === null ? undefined : overlay?.pairs.find((pp) => pp.latest === look.workoutId);
  /** This week's Monday — the row the empty-week caption sits under (cut 2). */
  const thisMonday = mondayKey(today, 0);
  /* 🔴 JOB 3 STEP 5 — ONE CALENDAR. The Tasks calendar under this one drew the same grid again; a day's `+ N tasks` line
     now opens THAT DAY's tasks window (`DayTasksWindow`: each task's `Remove…`, and `Add a task`). Focus returns to the line
     that opened it once the window is gone, as the workout window's does. */
  const [tasksDay, setTasksDay] = useState<string | null>(null);
  /* Week editor stage 2 — the library panel (remembered per coach, `viewPrefs`) and the week being saved as a program. */
  const [libOpen, setLibOpenState] = useState(() => readPref("weekLibrary") === "1");
  const setLibOpen = (on: boolean) => { setLibOpenState(on); writePref("weekLibrary", on ? "1" : "0"); };
  const [savingWeek, setSavingWeek] = useState<WeekToSave | null>(null);
  const tasksOpener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (tasksDay !== null || tasksOpener.current === null) return;
    tasksOpener.current.focus();
    tasksOpener.current = null;
  }, [tasksDay]);
  const onOpenTasks = onTasksChanged === undefined ? undefined : (day: string) => {
    tasksOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setTasksDay(day);
  };

  return (
    <section className={`calendar training-calendar${stickyBar ? " has-sticky-bar" : ""}`} aria-labelledby="calendar-h"
             style={barH === null ? undefined : { "--calendar-bar-h": `${String(barH)}px` } as React.CSSProperties}>
      <h2 id="calendar-h" className="ht-visually-hidden">Training calendar</h2>

      <div ref={bar} className="calendar-bar">
        {/* 🔴 W21 (B2) — `Today`, `Assignment ⇄ History` and the three spans are the DESKTOP's
            controls and are `display: none` at phone width (Fable's ruling on the first sheet:
            they are *"the desktop calendar controls"* sitting above the app's day rows). They
            are HIDDEN rather than unmounted: `Today` still returns a coach who has paged out,
            and at phone width that job is done by the range's own jump instead. */}
        <button type="button" className="ht-button calendar-today"
                onClick={() => { setView({ span: view.span, date: mondayKey(today, 0) }); }}
                disabled={offset === 0}>
          Today
        </button>
        {/* REAL BUTTONS WITH REAL NAMES. Theirs are bare chevrons; a screen reader reads "button". */}
        <button type="button" className="ht-button calendar-step calendar-step-prev" aria-label="Previous weeks"
                onClick={() => setOffset((n) => n - 1)}>‹</button>
        {/* 🔴 THE RANGE IS THE CONTROL — the app's own answer, and its own comment says why:
            Everfit's answer to *"show me three months ago"* is a separate History screen, ninety
            mostly-empty rows between you and the week you wanted. Tap the date, pick a day, land
            on that week.
            ⚠️ THE `⌄` IS A REAL DISCLOSURE AND THE PANEL IT OPENS IS A REAL `<input type="date">`.
            A caret that opened nothing would be W22(g)'s defect — a control documented as the
            cheapest falsifiable win, with no caller — drawn on purpose this time.
            ⚠️ `aria-live` MOVES TO THE BUTTON'S OWN TEXT. The range is still announced when it
            changes; it is now announced from inside a control rather than from a paragraph
            beside one. */}
        <p className="ht-body calendar-range">
          {isPhone ? (
            <button type="button" className="calendar-range-jump"
                    aria-expanded={jumping}
                    onClick={() => { setJumping((j) => !j); }}>
              <span aria-live="polite">{range}</span>
              <span aria-hidden="true" className="calendar-range-caret">⌄</span>
              <span className="ht-visually-hidden"> — choose a week</span>
            </button>
          ) : (
            /* ⚠️ THE DESKTOP KEEPS THE PLAIN PARAGRAPH, AND THAT IS NOT TIMIDITY. The jump exists
               because the phone loses `Today` and the spans; the desktop still has both, so a
               button here would be a third route to a place two controls already reach — and it
               would MOVE a screen this change is not about. */
            <span aria-live="polite">{range}</span>
          )}
        </p>
        <button type="button" className="ht-button calendar-step calendar-step-next" aria-label="Next weeks"
                onClick={() => setOffset((n) => n + 1)}>›</button>

        {isPhone && jumping && (
          <p className="calendar-jump">
            <label className="ht-caption calendar-jump-label" htmlFor="calendar-jump">
              Go to the week of
            </label>
            {/* ⚠️ THE VALUE IS A DAY AND THE EFFECT IS A WEEK, and the input says so in its label.
                Any day in a week lands on that week — `mondayKey` of the chosen day — because a
                coach thinking "the week of the 3rd" should not have to know which day was the
                Monday. An unparseable value changes nothing rather than guessing. */}
            <input id="calendar-jump" type="date" className="ht-input calendar-jump-input"
                   value={weeks[0]?.mondayKey ?? ""}
                   onChange={(e) => {
                     const picked = dayKey(e.target.value);
                     if (picked === null) return;
                     setView({ span: view.span, date: mondayKey(new Date(picked + "T00:00:00Z")) });
                     setJumping(false);
                   }} />
          </p>
        )}

        {/* ⚠️ THE COPIED `Assignment ⇄ History` PAIR IS REMOVED (W72 step 2, Fable FIX 4): his 2026-09-02 rule
            "the past is the progress page" — the Progress tab carries what History showed. W107: the `Open all`
            button went too — every card's list is always drawn. */}

        {/* THE SPAN AS CHIPS (Proposal 7 rule 6; their radios gone): one pressed at a time, `1 · 2 · 4 weeks`. */}
        <div className="ours-chips calendar-spans" role="group" aria-label="How many weeks">
          {SPANS.map((s) => (
            <button key={s} type="button" className="ours-chip" aria-pressed={span === s} onClick={() => setSpan(s)}>
              {s === 4 ? "4 weeks" : s}
            </button>
          ))}
        </div>

        {/* 🔴 W137 (R1) — THE PAGE'S ONE VIEW CONTROL, beside the spans: his `Minimized · Extended`. Radios underneath (a
            segmented control choosing one of two IS a radio group), drawn in the spans' own segmented well. */}
        <SegmentRadios name={`week-mode-${clientId}`} legend="View" className="calendar-mode"
                       options={WEEK_MODES.map((m) => ({ value: m, label: WEEK_MODE_LABEL[m] }))}
                       value={mode} onChange={setMode} />
        {/* 🔴 JOB 3 · WILLIAM (2026-10-15, his "a little overwhelming with all the options"): THE BAR IS THREE GROUPS — the
            range (Today ‹ › ), what is shown (spans · Minimized/Extended), and the page's actions — HIG Toolbars: "aim for a
            maximum of three" groups, "a More menu" for the less used. Scroll · Coach notes · Next week's changes · Print moved
            into ONE `Options` panel, unchanged inside it (the same radios, the same switch, the same Print). */}
        {onAddTask !== undefined && (
          <button type="button" className="ht-button task-calendar-add"
                  onClick={() => onAddTask(dayKeyOf(today))}>
            Add a task today
          </button>
        )}
        {(layout.scrollControl || mode === "extended" || overlayToggle !== undefined) && (
          <CalendarOptions>
            {/* R5b — in Extended only, and never on a narrow screen (one scroll there): each day on its own, or all together. */}
            {layout.scrollControl && (
              <SegmentRadios name={`week-scroll-${clientId}`} legend="Scroll" showLegend className="calendar-scroll-mode"
                             options={SCROLL_MODES.map((m) => ({ value: m, label: SCROLL_MODE_LABEL[m] }))}
                             value={scroll} onChange={setScroll} />
            )}
            {/* His word 2026-10-15 — in Extended only (the minimized card prints no note): the coach's exercise notes on or off. */}
            {mode === "extended" && (
              <SegmentRadios name={`week-notes-${clientId}`} legend="Coach notes" showLegend className="calendar-notes-mode"
                             options={NOTES_MODES.map((m) => ({ value: m, label: NOTES_MODE_LABEL[m] }))}
                             value={notes} onChange={setNotes} />
            )}
            {/* THE PROPOSALS OVERLAY'S SWITCH (Fable FIX 1) — the coach's own choice. */}
            {overlayToggle !== undefined && (
              <label className="calendar-overlay-toggle ht-caption">
                <input type="checkbox" checked={overlayToggle.on} disabled={overlayToggle.disabled}
                       onChange={(e) => overlayToggle.onToggle(e.currentTarget.checked)} />
                <span>Next week&rsquo;s changes</span>
              </label>
            )}
            {/* 🔴 QUEUE N2b — PRINT, in Extended only: the one view that reads each workout's contents (HIG: offer printing
                only when there is something to print). N2b-2: the week's workouts as Everfit's grid (`WorkoutPrintSheet`). */}
            {mode === "extended" && printable.length > 0 && (
              <button type="button" className="ht-button calendar-print" onClick={() => { printWorkouts(printable); }}>Print</button>
            )}
          </CalendarOptions>
        )}
      </div>

      {/* 🔴 W22(e) — MON–SUN COLUMN HEADERS. Theirs draws them and neither of our calendars did,
          so a coach counted across from the date numbers to work out which column was Saturday.
          ⚠️ `aria-hidden`, and the reason is that the grid is not a `<table>`: each cell already
          carries the full date in its `+` button's accessible name (`Assign work on Sat 19 Sep`),
          so a screen reader that also read a header row would hear the weekday twice with no
          structural relationship between the two. The row is for the EYE.
          ⚠️ It is drawn ONCE, above the first week, and not per week — seven words repeated four
          times down a four-week view is four times the ink for the same fact. */}
      <div className="calendar-week calendar-weekdays" aria-hidden="true">
        {WEEKDAYS.map((d) => (
          <span key={d} className="ht-overline calendar-weekday">{d}</span>
        ))}
      </div>

      {/* 🔴 W137 cut 2 (R6) — ONE drag area over the visible range, both views: a workout's 4-arrow handle carries it to
          another day (a reschedule) or above/below another on its day (`day_position`). Without a page handler it is drawn
          disabled — no handles — rather than half-wired. */}
      <DayDragArea arrangement={dragArrangement} stored={storedOrder} titleOf={(id) => itemById.get(id)?.name ?? "Workout"}
                   onDrop={onMoveWorkout ?? (async () => false)} disabled={onMoveWorkout === undefined}>
      <ShownWeeks weeks={weeks} itemById={itemById}>{(weeks) => (<>
      {layout.kind === "squares" && weeks.map((w) => (
        <Fragment key={w.mondayKey}>
        <div className="calendar-week">
          {w.days.map((d, di) => (
            <DayCard key={d.dayKey} day={d} weekday={WEEKDAYS[di] ?? ""} span={span}
                     trainingDays={trainingDays} onAddTo={onAddTo} onAssignmentAction={onAssignmentAction}
                     onOpenTasks={onOpenTasks} onAddTask={onAddTask} overlay={overlay}
                     proposalCount={proposalCounts[d.dayKey] ?? 0}
                     onLook={(workoutId, opener) => { lookOpener.current = opener; setLook({ workoutId, dayKey: d.dayKey }); }} />
          ))}
        </div>
        {w.mondayKey === thisMonday && emptyWeekLine !== undefined && <p className="ht-caption calendar-week-empty">{emptyWeekLine}</p>}
        </Fragment>
      ))}

      {/* 🔴 W137 (R3–R5, R5b) — EXTENDED: seven day columns per week, hairlines between them, each day's workouts drawn as
          the phone overview's content without the phone, plus each exercise's rest and coach note. */}
      {layout.kind === "columns" && (
        <WeekEditing weeks={weeks} trees={trees} clientId={clientId} write={onWriteWeek} assignWorkout={onAssignWorkout}
                     onStructureChanged={() => { onWeekStructureChanged?.(); }}
                     onTreesStale={bumpTrees}>
        <div className={onWriteWeek !== undefined && libOpen ? "week-split has-library" : "week-split"}>
        <ExtendedWeeks weeks={weeks} span={span} layout={layout} trees={trees} trainingDays={trainingDays} said={said}
                       onAddTo={onAddTo} onAssignmentAction={onAssignmentAction} onOpenTasks={onOpenTasks} onAddTask={onAddTask}
                       overlay={overlay} proposalCounts={proposalCounts} onEditExercise={onEditExercise} notes={notes === "show"}
                       editable={onWriteWeek !== undefined}
                       onSaveWeek={onWriteWeek === undefined ? undefined : (w) => { setSavingWeek(w); }}
                       library={onWriteWeek === undefined ? undefined : { open: libOpen, toggle: () => { setLibOpen(!libOpen); } }}
                       emptyCaption={emptyWeekLine === undefined ? undefined : { mondayKey: thisMonday, line: emptyWeekLine }}
                       onLook={(workoutId, dayKey, opener) => { lookOpener.current = opener; setLook({ workoutId, dayKey }); }} />
        {onWriteWeek !== undefined && libOpen && <WeekLibrary onClose={() => { setLibOpen(false); }} />}
        </div>
        </WeekEditing>
      )}
      </>)}</ShownWeeks>
      </DayDragArea>

      {look !== null && (
        <WorkoutOverviewWindow key={look.workoutId} workoutId={look.workoutId}
          day={{
            eyebrow: shortDate(look.dayKey),
            onClose: () => { setLook(null); },
            /* W107's click on an exercise, kept: the window closes and the page's own editor opens on that row. */
            onPickRow: onEditExercise === undefined ? undefined : (rowId) => {
              const workoutId = look.workoutId;
              lookOpener.current = null;
              setLook(null);
              onEditExercise(workoutId, rowId);
            },
            /* W72 step 2 — with the overlay on and this workout paired, next week's moves (Keep · Change… · Hold), drawn in
               the window above the workout; `Change…` opens the same editor. */
            moves: lookPair === undefined ? undefined : (
              <ProposalTiles latestId={lookPair.latest} previousId={lookPair.previous}
                             onChange={onEditExercise === undefined ? undefined : (workoutId, rowId) => {
                               lookOpener.current = null;
                               setLook(null);
                               onEditExercise(workoutId, rowId);
                             }}
                             onWritten={onOverlayWritten} />
            ),
          }} />
      )}

      {savingWeek !== null && (
        <SaveWeekAsProgram week={savingWeek} clientName={clientName} onClose={() => { setSavingWeek(null); }} />
      )}

      {tasksDay !== null && onTasksChanged !== undefined && (
        <DayTasksWindow dayKey={tasksDay} tasks={(itemsByDay.get(tasksDay) ?? []).filter((i) => i.isTask)}
                        onAddTask={onAddTask} onChanged={onTasksChanged} onClose={() => { setTasksDay(null); }} />
      )}
    </section>
  );
}

type CalendarDay = ReturnType<typeof calendarWeeks>[number]["days"][number];

/** Week editor stage 1 — the editor around the Extended week, where the page can write (`write`); otherwise the children
 *  alone (the Program board, a page that only reads). Its menus' days are the VISIBLE days: a row moves within what is on
 *  screen. A workout on a trained day is a record — locked. */
function WeekEditing({ weeks, trees, clientId, write, onStructureChanged, onTreesStale, assignWorkout, children }: {
  assignWorkout?: (w: { id: string; rootId: string; name: string }, dayKey: string, index: number) => Promise<{ said: string; ok: boolean }>;
  weeks: ReturnType<typeof calendarWeeks>;
  trees: WeekTrees;
  clientId: string;
  write?: WeekWrite;
  onStructureChanged: () => void;
  onTreesStale: () => void;
  children: React.ReactNode;
}) {
  const days = useMemo<EditorDay[]>(() => weeks.flatMap((w) => w.days).map((d) => ({
    dayKey: d.dayKey,
    workouts: d.items.filter((i) => !i.isTask && i.workoutId !== undefined)
      .map((i) => ({ workoutId: i.workoutId!, name: i.name, trained: i.trained })),
  })), [weeks]);
  const trained = useMemo(() => new Set(days.flatMap((d) => d.workouts.filter((w) => w.trained).map((w) => w.workoutId))), [days]);
  const base = useMemo(() => (trees.status === "ready" ? trees.trees : new Map()), [trees]);
  if (write === undefined) return <>{children}</>;
  return (
    <WeekEditorProvider base={base} trained={trained} days={days} clientId={clientId} write={write} assignWorkout={assignWorkout}
                        onStructureChanged={onStructureChanged} onTreesStale={onTreesStale}>
      {children}
    </WeekEditorProvider>
  );
}

/** The drag's arrangement of the visible range: each day's ASSIGNMENTS (tasks are not dragged) in their stored order. */
function dayArrangementOf(weeks: ReturnType<typeof calendarWeeks>): DayArrangement {
  const out: Record<string, string[]> = {};
  for (const w of weeks) for (const d of w.days) out[d.dayKey] = d.items.filter((i) => i.assignmentId !== undefined).map((i) => i.id);
  return out;
}

/** The weeks AS THE DRAG DRAWS THEM: while a workout is carried (or has just landed) each day's assignments come from the
 *  drag's live arrangement, so the gap travels across days; the day's tasks stay where they are. */
function ShownWeeks({ weeks, itemById, children }: {
  weeks: ReturnType<typeof calendarWeeks>;
  itemById: ReadonlyMap<string, CalendarItem>;
  children: (weeks: ReturnType<typeof calendarWeeks>) => React.ReactNode;
}) {
  const { arrangement } = useDayArrangement();
  const shown = weeks.map((w) => ({
    ...w,
    days: w.days.map((d) => {
      const ids = arrangement[d.dayKey];
      if (ids === undefined) return d;
      const moved = ids.map((id) => itemById.get(id)).filter((i): i is CalendarItem => i !== undefined);
      return { ...d, items: [...moved, ...d.items.filter((i) => i.assignmentId === undefined)] };
    }),
  }));
  return <>{children(shown)}</>;
}

/**
 * 🔴 W115 — ONE DAY CARD, ONE STATE. A rounded square, every card equal, whatever it holds and whatever the pointer or the
 * keyboard does: the date and its mark, each workout's TITLE with a magnifier beside it (the workout opens in a window), the
 * `⋯` where there is no hover and W108's words at the item's foot where there is (an overlay — it reserves nothing), the
 * overlay's `N proposals` mark and the tasks fold. Nothing grows, nothing is hidden inside it waiting to be revealed.
 */
function DayCard({ day: d, weekday, span, trainingDays, onAddTo, onAssignmentAction, onOpenTasks, onAddTask, overlay,
                   proposalCount, onLook }: {
  day: CalendarDay;
  weekday: string;
  span: Span;
  trainingDays: Set<string>;
  onAddTo?: (dayKey: string) => void;
  onAssignmentAction?: (assignmentId: string, item: MenuItem) => void;
  onOpenTasks?: (dayKey: string) => void;
  onAddTask?: (dayKey: string) => void;
  overlay?: { pairs: readonly OverlayPair[]; counts: Readonly<Record<string, number>> };
  proposalCount: number;
  onLook: (workoutId: string, opener: HTMLButtonElement) => void;
}) {
  const { workouts, tasks, state } = dayParts(d, trainingDays);
  return (
    <DropDay dayKey={d.dayKey} ids={workouts.map((i) => i.id)}>{(dropRef, isOver) => (
    <div ref={dropRef} className={`calendar-day is-${state}${d.isToday ? " is-today ours-sweep" : ""}${d.isPast ? " is-past" : ""}${isOver ? " is-drop-over" : ""}`}>
      {/* The FACE: what the card says, pinned to the square and clipped (direction.css), so a long title never stretches it. */}
      <div className="calendar-day-face">
        <div className="calendar-day-head">
          {/* The weekday for the phone's row layout — the same list as the column header, by index. */}
          <span className="ht-overline calendar-day-weekday" aria-hidden="true">
            {weekday}
          </span>
          <DayHeadMarks d={d} span={span} state={state} onAddTo={onAddTo} />
        </div>
        {/* W107 — NO PICTURES ON THE CARD. W115 — only the TITLES; the workout itself is the window's. */}
        <ul className="calendar-day-items">
          {workouts.map((i) => (
            <DragItem key={i.id} id={i.id} locked={i.trained} className="ht-caption calendar-item">
              <WorkoutTitle item={i} d={d} onAssignmentAction={onAssignmentAction} onLook={onLook} />
            </DragItem>
          ))}
        </ul>
        <DayFoot d={d} tasks={tasks.length} proposalCount={proposalCount} overlayOn={overlay !== undefined}
                 onOpenTasks={onOpenTasks} onAddTask={onAddTask} />
      </div>
    </div>
    )}</DropDay>
  );
}

/** The day's head after its weekday: the date, the done tick, the screen reader's state word and the `+` — ONE drawing for
 *  the square card and the extended column (W137), so the two views cannot mark a day two ways. */
function DayHeadMarks({ d, span, state, onAddTo }: {
  d: { dayKey: string };
  span: Span;
  state: ReturnType<typeof cellState>;
  onAddTo?: (dayKey: string) => void;
}) {
  return (
    <>
      {/* 🔴 THE FULL WEEKDAY AND HIS `21/9` (Proposal 2). */}
      {/* `cellHead` = the word + `dayMonthLabel`, drawn as two spans so the phone (whose gutter says
          `MON`) can drop the word without a second spelling of the date. */}
      <span className="ht-data-small calendar-day-num">
        <span className="calendar-day-word">{cellHeadWord(d.dayKey, span)}</span> {dayMonthLabel(d.dayKey)}
      </span>
      {/* 🔴 HIS CHANGE (2): THE DONE TICK SITS TOP-RIGHT OF THE CONTAINER, never in a title. */}
      {state === "done" && (
        <span className="calendar-day-tick" aria-hidden="true">
          <Icon name="check" size={12} stroke={3} />
        </span>
      )}
      <span className="ht-visually-hidden">{state === "done" ? "trained" : state === "missed" ? "not trained" : ""}</span>
      {/* IN THE DOM, DRAWN ON HOVER (W22(g)) — reachable to the keyboard, revealed by focus. */}
      {onAddTo !== undefined && (
        <button type="button" className="calendar-add"
                aria-label={`Assign work on ${shortDate(d.dayKey)}`}
                onClick={() => onAddTo(d.dayKey)}>+</button>
      )}
    </>
  );
}

/** The day's foot: the overlay's `N proposals` mark and the tasks fold — shared by both views (W137). */
function DayFoot({ d, tasks, proposalCount, overlayOn, onOpenTasks, onAddTask }: {
  d: { dayKey: string };
  tasks: number;
  proposalCount: number;
  overlayOn: boolean;
  onOpenTasks?: (dayKey: string) => void;
  onAddTask?: (dayKey: string) => void;
}) {
  return (
    <>
      {/* W72 step 2 — the overlay's mark on the cell: the smallest thing that says next week moved here (its moves are
          drawn in the window the magnifier opens). */}
      {overlayOn && proposalsLabel(proposalCount) !== null && (
        <span className="ht-caption calendar-proposals">{proposalsLabel(proposalCount)}</span>
      )}
      {/* W71 — ONE fold line for the day's tasks. Job 3 step 5: it opens THIS day's tasks window (the Tasks calendar is
          folded into this one), where a task is removed and another added. */}
      {tasks > 0 && (onOpenTasks === undefined ? (
        <span className="ht-caption calendar-tasks-fold">{tasksFoldLabel(tasks)}</span>
      ) : (
        <button type="button" className="ht-caption calendar-tasks-fold"
                title="Tasks — open to manage"
                aria-label={`${tasksFoldLabel(tasks)} on ${shortDate(d.dayKey)} — open to manage them`}
                onClick={() => onOpenTasks(d.dayKey)}>
          {tasksFoldLabel(tasks)}
        </button>
      ))}
      {/* Job 3 step 5 — A DAY WITH NO TASKS carries a quiet `+ Task`, revealed as the cell's `+` is (hover, focus, a screen
          with no hover), straight to the task form for THIS day: the first task on a day is one click. */}
      {onAddTask !== undefined && tasks === 0 && (
        <button type="button" className="ht-caption calendar-tasks-fold calendar-task-add"
                aria-label={`Add a task on ${shortDate(d.dayKey)}`}
                onClick={() => onAddTask(d.dayKey)}>+ Task</button>
      )}
    </>
  );
}

/**
 * 🔴 W137 (R1) — A WORKOUT'S TITLE LINE, IN BOTH VIEWS: the name, then the `⋯` (drawn only where there is no hover, W108).
 * THE EYE IS GONE (his: *"so instead of the eye, we need a global button … of extended and minimized"*). The NAME is the
 * button that opens the workout in the overview window (W115's window, unchanged) — it is the only door to next week's
 * moves when the proposals overlay pairs a workout and to a row's exercise editor, which R9 keeps — so it is a real
 * `<button>` with the eye's old name (`Preview … on …`), quiet, no glyph. `View` in the `⋯` stays the day's door.
 * 10-16 (the hover row's brief) — W108's foot WORDS are gone from the card: hovered, they covered its date and name (MEASURED,
 * 1024 + 1440). Where there is hover the item shows ONE row under its title — pencil · Copy · 4-arrow · `⋯` — and the `⋯` holds
 * the words no glyph is (`cardMenuItems`, `glyphs`); where there is none, the `⋯` alone, holding every word.
 */
function WorkoutTitle({ item: i, d, onAssignmentAction, onLook }: {
  item: CalendarItem;
  d: { dayKey: string };
  onAssignmentAction?: (assignmentId: string, item: MenuItem) => void;
  onLook: (workoutId: string, opener: HTMLButtonElement) => void;
}) {
  /* The stylesheet draws the glyphs under `(any-hover: hover)`; the `⋯` asks the same query (`pointerKind.ts`). */
  const glyphs = useAnyHover();
  return (
    <div className="calendar-item-top">
      {i.workoutId === undefined && <span className="calendar-item-name">{i.name}</span>}
      {i.workoutId !== undefined && (
        <button type="button" className="calendar-item-name calendar-item-open" title="Preview"
                aria-label={`Preview ${i.name} on ${shortDate(d.dayKey)}`}
                onClick={(e) => { onLook(i.workoutId!, e.currentTarget); }}>
          {i.name}
        </button>
      )}
      {/* 10-16 — the glyphs ride in ONE span. Where there is hover it is the row UNDER the title (both views), shown on the
          item's hover or focus, over the card's ground: the name owns its line at rest and is never covered (the hover row's
          brief). Where there is none it is `display: contents` beside the name, and only its `⋯` is drawn. */}
      <span className="calendar-item-tools">
        {/* 🔴 W137 R6b (his: "a copy icon and a 4 arrow one next to each workout title") — COPY then the 4-ARROW, visible at
            rest, on every ASSIGNMENT (a task is neither copied nor dragged here). Copy opens the day window in its copy mode;
            the 4-arrow is the drag handle, and a plain click on it opens `Move…`. A trained day is a record: no handle. */}
        {/* William's item 4 — the PENCIL, at rest before Copy: the builder on THIS day's copy. Not on a trained day (a record). */}
        {canEditWorkout({ isTask: i.isTask, trained: i.trained }) && i.assignmentId !== undefined && i.workoutId !== undefined
          && onAssignmentAction !== undefined && (
          <button type="button" className="day-copy day-edit" title="Edit workout"
                  aria-label={`Edit ${i.name} on ${dayWords(d.dayKey)}`}
                  onClick={() => { onAssignmentAction(i.assignmentId!, EDIT_ITEM); }}>
            <Icon name="pencil" size={14} stroke={2} />
          </button>
        )}
        {!i.isTask && i.assignmentId !== undefined && onAssignmentAction !== undefined && (
          <button type="button" className="day-copy" title="Copy" aria-label={copyLabel(i.name, dayWords(d.dayKey))}
                  onClick={() => { onAssignmentAction(i.assignmentId!, COPY_ITEM); }}>
            <Icon name="copy" size={14} stroke={2} />
          </button>
        )}
        {!i.isTask && i.assignmentId !== undefined && (
          <MoveHandle label={moveHandleLabel(i.name, dayWords(d.dayKey))}
                      onActivate={onAssignmentAction === undefined ? undefined : () => { onAssignmentAction(i.assignmentId!, MOVE_ITEM); }} />
        )}
        {/* W11b — the `⋯`, on ASSIGNMENTS only, on both pointer kinds (10-16, the hover row). Hover: the row's last glyph,
            listing what no glyph does. No hover: the three above are not drawn (they left the name 0 px at 1024), so its list
            carries Edit workout and Copy to…. */}
        {onAssignmentAction !== undefined && !i.isTask && i.assignmentId !== undefined && (
          <AssignmentMenu
            /* `hiddenFromClient` is false here by construction: this grid SKIPS hidden
               assignments outright, so its menu can never offer Unhide. */
            assignment={{ id: i.assignmentId, hiddenFromClient: false, trained: i.trained }}
            items={cardMenuItems({ hiddenFromClient: false, trained: i.trained, glyphs,
                                   canEdit: canEditWorkout({ isTask: i.isTask, trained: i.trained }) && i.workoutId !== undefined })}
            label={`${i.name} on ${shortDate(d.dayKey)}`}
            onChoose={(item) => onAssignmentAction(i.assignmentId!, item)} />
        )}
      </span>
    </div>
  );
}

/** W71 — a day's workouts first and its tasks folded into ONE line, and the day's mark — asked ONCE for both views. */
function dayParts(d: CalendarDay, trainingDays: Set<string>) {
  const { workouts, tasks } = workoutsThenTasks(d.items);
  const state = cellState({
    isToday: d.isToday, isPast: d.isPast,
    trained: trainingDays.has(d.dayKey), hasTraining: workouts.length > 0,
  });
  return { workouts, tasks, state };
}

/** A segmented control choosing one of a few — radios underneath (the `VisibleRangeControl` pattern: a group with a name,
 *  arrow keys between the options, the input visually hidden INSIDE its tile, never `display: none`). */
export function SegmentRadios<V extends string>({ name, legend, showLegend = false, className, options, value, onChange }: {
  name: string;
  legend: string;
  showLegend?: boolean;
  className: string;
  options: readonly { value: V; label: string }[];
  value: V;
  onChange: (next: V) => void;
}) {
  return (
    <fieldset className={`calendar-segment ${className}`}>
      <legend className={showLegend ? "ht-caption calendar-segment-legend" : "ht-visually-hidden"}>{legend}</legend>
      <div className="calendar-segment-well">
        {options.map((o) => (
          <label key={o.value} className={value === o.value ? "calendar-segment-option is-on" : "calendar-segment-option"}>
            <input type="radio" name={name} value={o.value} checked={value === o.value}
                   onChange={() => { onChange(o.value); }} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * 🔴 W137 (R3–R5, R5b) — THE EXTENDED WEEKS. One row of seven day columns per week, each row under a quiet week label;
 * straight hairlines between the days, no card per day. A column's head is its weekday and date with the same marks as the
 * square card; under it each workout is the phone overview's content without the phone (`ColumnWorkout`), then the day's
 * foot. `Each day`: every column scrolls on its own (`DayColumn`), the row as tall as the viewport's remaining height at one
 * week, capped at two and four. `Together`: the columns grow, the page scrolls them as one, the heads stay pinned, ONE `↑`.
 */
function ExtendedWeeks({ weeks, span, layout, trees, trainingDays, onAddTo, onAssignmentAction, onOpenTasks, onAddTask,
                         overlay, proposalCounts, onLook, onEditExercise, emptyCaption, notes, editable = false, onSaveWeek, library, said }: {
  /** CARRY-FEEDBACK — where the client will meet the coach's last feedback: per exercise row (A), per assignment (B). */
  said: CarryFeedback;
  /** Week editor stage 2 — the library panel's switch, on the FIRST week's label line (the bar keeps its three groups — job 3,
   *  William 10-15 "a little overwhelming"; MEASURED 10-19: a fourth item wrapped `Options` to its own line at 1024). */
  library?: { open: boolean; toggle: () => void };
  /** Week editor stage 1 — rows can be dropped on a column (a day). */
  editable?: boolean;
  /** Week editor stage 2 — `Save as program…` on each week's label line. */
  onSaveWeek?: (week: WeekToSave) => void;
  /** His 2026-10-15 switch — the coach's exercise notes drawn in the columns or not. */
  notes: boolean;
  /** Cut 2 — this week's empty sentence and the row it sits under. */
  emptyCaption?: { mondayKey: string; line: string };
  onEditExercise?: (workoutId: string, exerciseRowId: string) => void;
  weeks: ReturnType<typeof calendarWeeks>;
  span: Span;
  layout: ReturnType<typeof weekLayout>;
  trees: WeekTrees;
  trainingDays: Set<string>;
  onAddTo?: (dayKey: string) => void;
  onAssignmentAction?: (assignmentId: string, item: MenuItem) => void;
  onOpenTasks?: (dayKey: string) => void;
  onAddTask?: (dayKey: string) => void;
  overlay?: { pairs: readonly OverlayPair[]; counts: Readonly<Record<string, number>> };
  proposalCounts: Readonly<Record<string, number>>;
  onLook: (workoutId: string, dayKey: string, opener: HTMLButtonElement) => void;
}) {
  /* Cut 1b: a row's height is the viewport minus the sticky controls row (`--calendar-bar-h`, measured by the calendar) —
     the CSS's, so once the coach scrolls to the calendar each day column is nearly the screen's height. */
  const root = useRef<HTMLDivElement>(null);
  /* `Together`: the page's one `↑`, drawn only once the week's top has scrolled out of sight. */
  const [past, setPast] = useState(false);
  useEffect(() => {
    if (!layout.pageTopButton) { setPast(false); return; }
    const on = () => { const el = root.current; setPast(el !== null && el.getBoundingClientRect().top < -8); };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); };
  }, [layout.pageTopButton]);
  /* Week editor stage 1 — a copy a move just created, drawn on its day before the page's read lists it. */
  const ed = useWeekEditor();
  const cls = ["week-ext", `is-rows-${layout.rowHeight}`, layout.dayScrolls ? "is-day-scroll" : "is-page-scroll",
    layout.stickyHeads ? "has-sticky-heads" : "", layout.oneDayPerRow ? "is-one-per-row" : ""].filter(Boolean).join(" ");
  return (
    <div ref={root} className={cls}>
      {weeks.map((w) => (
        /* A week with nothing in it is not given a full scrolling row — its heads and their `+` are what it holds. */
        <section key={w.mondayKey} aria-label={rangeLabel([w], shortDate)}
                 className={w.days.every((d) => d.items.length === 0) ? "week-ext-week is-empty" : "week-ext-week"}>
          {onSaveWeek !== undefined ? (
            <div className="week-ext-label-row">
              <p className="ht-caption week-ext-label" aria-hidden="true">{rangeLabel([w], shortDate)}</p>
              {library !== undefined && w === weeks[0] && (
                <button type="button" className="ht-button ht-button-quiet week-lib-toggle" aria-pressed={library.open} onClick={library.toggle}>
                  <Icon name="library" size={14} /> Library
                </button>
              )}
              <button type="button" className="ours-text-action week-ext-save" aria-label={`Save ${rangeLabel([w], shortDate)} as a program`}
                      onClick={() => {
                        onSaveWeek({ mondayKey: w.mondayKey, days: w.days.map((d) => ({
                          dayKey: d.dayKey,
                          workouts: d.items.filter((i) => !i.isTask && i.workoutId !== undefined).map((i) => ({ workoutId: i.workoutId!, name: i.name })),
                        })) });
                      }}>Save as program…</button>
            </div>
          ) : weeks.length > 1 && <p className="ht-caption week-ext-label" aria-hidden="true">{rangeLabel([w], shortDate)}</p>}
          <div className="week-ext-row">
            {w.days.map((d) => {
              const { workouts, tasks, state } = dayParts(d, trainingDays);
              return (
                <DropDay key={d.dayKey} dayKey={d.dayKey} ids={workouts.map((i) => i.id)}>{(dropRef, isOver) => (
                <DayColumn dayKey={d.dayKey} scrolls={layout.dayScrolls} rootRef={dropRef} dropDay={editable && !d.items.some((i) => i.trained)}
                           label={`${cellHeadWord(d.dayKey, 1)} ${dayMonthLabel(d.dayKey)}`}
                           className={`is-${state}${d.isToday ? " is-today" : ""}${d.isPast ? " is-past" : ""}${isOver ? " is-drop-over" : ""}`}
                           head={<div className="calendar-day-head"><DayHeadMarks d={d} span={span} state={state} onAddTo={onAddTo} /></div>}>
                  <ul className="week-col-items">
                    {workouts.map((i) => (
                      <DragItem key={i.id} id={i.id} locked={i.trained} className="ht-caption week-col-item">
                        <ColumnWorkout tree={i.workoutId} trees={trees} notes={notes} said={said.rows}
                          workoutSaid={i.assignmentId === undefined ? undefined : said.workouts.get(i.assignmentId)}
                          /* R3b: the exercise name opens it to be SEEN (ExerciseLookWindow, Fable's ruling); its `Edit sets`
                             is the Week's existing one-exercise door (W107/W115: the per-day set editor). */
                          onEditSets={onEditExercise === undefined || i.workoutId === undefined ? undefined
                            : (rowId) => { onEditExercise(i.workoutId!, rowId); }}
                          title={
                          <div className="week-col-title">
                            <WorkoutTitle item={i} d={d} onAssignmentAction={onAssignmentAction}
                                          onLook={(id, opener) => { onLook(id, d.dayKey, opener); }} />
                          </div>
                        } />
                      </DragItem>
                    ))}
                    {(ed?.pending ?? []).filter((p) => p.dayKey === d.dayKey && !workouts.some((i) => i.workoutId === p.workoutId)).map((p) => (
                      <li key={p.workoutId} className="ht-caption week-col-item">
                        <ColumnWorkout tree={p.workoutId} trees={trees} notes={notes}
                                       title={<div className="week-col-title"><span className="calendar-item-name">{p.name}</span></div>} />
                      </li>
                    ))}
                  </ul>
                  <DayFoot d={d} tasks={tasks.length} proposalCount={proposalCounts[d.dayKey] ?? 0}
                           overlayOn={overlay !== undefined} onOpenTasks={onOpenTasks} onAddTask={onAddTask} />
                </DayColumn>
                )}</DropDay>
              );
            })}
          </div>
          {emptyCaption !== undefined && w.mondayKey === emptyCaption.mondayKey && <p className="ht-caption calendar-week-empty">{emptyCaption.line}</p>}
        </section>
      ))}
      {layout.pageTopButton && past && (
        <button type="button" className="week-col-top week-ext-top" aria-label={BACK_TO_TOP_OF_WEEK} title={BACK_TO_TOP_OF_WEEK}
                onClick={() => { if (root.current !== null) backToStart(root.current, "page"); }}>
          <Icon name="arrow-up" size={16} stroke={2} />
        </button>
      )}
    </div>
  );
}

/**
 * W22(f) — one assignment's exercises, fetched when its cell is EXPANDED and not before.
 *
 * 🔴 **THE FETCH IS THE COMPONENT'S OWN LIFETIME, which is what makes "only the expanded cell"
 * true rather than merely intended.** This is mounted by the expanded branch, so a cell that is
 * never opened never asks the door — there is no cache to forget to bound and no effect keyed on
 * a flag that could drift from the render.
 *
 * ⚠️ **THREE STATES, AND THEY ARE THREE.** Loading is not "no tiles"; a REFUSED read (42501
 * from the door) is not an empty workout; and a workout that genuinely holds nothing is its own
 * answer. Collapsing any two of them is how a permissions failure comes to read as a rest day.
 */
/* 🔴 EXPORTED FOR W29(b), AND IT IS A REUSE RATHER THAN A SECOND COPY. The assign panel's preview
   asks the same question this cell does — *what will the athlete actually be given* — off the same
   door (`assigned_workout_tree`), the same walk (`workoutOverview`) and the same tiles
   (`dayTiles`). A component beside it would be the "two components, each internally correct" shape
   this repo is named after.
   ⚠️ ITS CLASS NAMES STAY `calendar-*` AND THAT IS SAID OUT LOUD. The tile IS the calendar's tile —
   one look, one component — and renaming them would touch a shipped surface, its stylesheet and
   two rulers for a cosmetic reason. The name records where it was born, not where it may be used. */
export function DayTiles({ workoutId, onPick }: {
  workoutId: string;
  onPick?: (exerciseRowId: string) => void;
}) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; sections: DaySection[] }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      const r = await fetchAssignedWorkoutTree(
        supabase.rpc.bind(supabase) as Parameters<typeof fetchAssignedWorkoutTree>[0], workoutId);
      if (cancelled) return;
      if (r.tree === null) { setState({ kind: "error", message: r.error }); return; }
      setState({ kind: "ready", sections: daySections(workoutOverview(r.tree)) });
    })();
    return () => { cancelled = true; };
  }, [workoutId]);

  if (state.kind === "loading") {
    return <p className="ht-caption calendar-tiles-note" aria-live="polite">Loading exercises…</p>;
  }
  if (state.kind === "error") {
    /* The door's own words. A refusal here is a policy fact worth seeing, and "nothing to show"
       would be this component inventing an answer about somebody's training. */
    return <p className="ht-caption calendar-tiles-note" role="alert">{state.message}</p>;
  }
  if (state.sections.length === 0) {
    return <p className="ht-caption calendar-tiles-note">No exercises in this workout yet.</p>;
  }
  const tile = (tile: DaySection["tiles"][number]) => (
    <li key={tile.key} className="calendar-tile">
      {tile.rows.map((r, ri) => (
        <span key={r.exerciseRowId}
              className={ri === 0 ? "calendar-tile-row" : "calendar-tile-row is-joined"}>
          {/* ⚠️ A TILE ROW IS A BUTTON ONLY WHEN SOMETHING CAN OPEN. W22(g) is this screen's
              own lesson about a control with no caller; without `onPick` these are spans. */}
          {onPick === undefined ? (
            <span className="calendar-tile-text">
              {r.count === "" ? "" : <b className="calendar-tile-count">{r.count}</b>}
              {r.name} <span className="calendar-tile-dose">{r.dose}</span>
            </span>
          ) : (
            <button type="button" className="calendar-tile-text"
                    onClick={() => onPick(r.exerciseRowId)}>
              {r.count === "" ? "" : <b className="calendar-tile-count">{r.count}</b>}
              {r.name} <span className="calendar-tile-dose">{r.dose}</span>
            </button>
          )}
        </span>
      ))}
    </li>
  );
  return (
    <ul className="calendar-tiles">
      {state.sections.map((sec) => (
        <li key={sec.key} className="calendar-section-group">
          {/* 🔴 THE SECTION TILE (§2b-ter depth 1, Fable's ruling 2026-09-24): the section's NAME,
              its FORMAT chip, and a foot line only when the section carries a number. The word is
              the preview's (`FORMAT_LABEL` / `formatHeadline`), never re-typed here. §6.5's loose
              run has a null name and draws no tile — its exercises stand alone. */}
          {/* W104(a): an unnamed section keeps its tile and FORMAT chip; only its name is absent. */}
          {!sec.implicit && (
            <div className="calendar-section-tile">
              <span className="calendar-section-head">
                {sec.name !== null && <span className="calendar-section-name">{sec.name}</span>}
                <span className="calendar-section-chip">{sec.formatLabel}</span>
              </span>
              {sec.formatLine !== null && (
                <span className="calendar-section-foot">
                  <Icon name="timer" className="calendar-section-glyph" size={12} stroke={2.2} />
                  {sec.formatLine}
                  <span className="calendar-section-sep" aria-hidden="true">|</span>
                  {exerciseCountLabel(sec.exerciseCount)}
                </span>
              )}
            </div>
          )}
          <ul className="calendar-tiles calendar-section-tiles">
            {sec.tiles.map(tile)}
          </ul>
        </li>
      ))}
    </ul>
  );
}
